import { useState, useRef, useEffect, useLayoutEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Action menu (the `…` dropdown next to a table row).
 *
 * Renders the menu via a portal anchored to `document.body` and positioned
 * with `fixed` coordinates derived from the trigger button's bounding rect.
 * This is what lets the menu escape the wrapping card's `overflow: hidden`
 * + any other ancestor clipping / stacking contexts — without it, menus on
 * the bottom rows of a table get clipped (see the AdminUsersPage report).
 *
 * Auto-flips above the trigger if there isn't enough room below.
 *
 * Usage:
 *
 *   <ActionMenu>
 *     <ActionItem icon={Lock} onClick={...}>Lock</ActionItem>
 *     <ActionItem icon={Trash2} danger onClick={...}>Delete</ActionItem>
 *   </ActionMenu>
 */

const MENU_WIDTH = 208; // matches w-52
const MARGIN = 4;

export function ActionMenu({
  children,
  label = 'Open actions',
}: {
  children: ReactNode;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; placeAbove: boolean } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Recompute position whenever we open or the window resizes.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    function place() {
      const rect = buttonRef.current!.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight ?? 240; // estimate before mount
      const spaceBelow = window.innerHeight - rect.bottom;
      const placeAbove = spaceBelow < menuHeight + MARGIN && rect.top > menuHeight + MARGIN;
      const top = placeAbove ? rect.top - menuHeight - MARGIN : rect.bottom + MARGIN;
      // Right-align: menu's right edge sits at the button's right edge,
      // but clamp so we never run off the viewport.
      const desiredLeft = rect.right - MENU_WIDTH;
      const left = Math.max(MARGIN, Math.min(desiredLeft, window.innerWidth - MENU_WIDTH - MARGIN));
      setCoords({ top, left, placeAbove });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (
        menuRef.current?.contains(e.target as Node) ||
        buttonRef.current?.contains(e.target as Node)
      ) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="btn-ghost text-xs"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          // Click anywhere on a menu button automatically closes via onActivate
          // inside ActionItem; this catches stray clicks on the menu shell.
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            top: coords?.top ?? -9999,
            left: coords?.left ?? -9999,
            width: MENU_WIDTH,
          }}
          className={cn(
            'z-[200] overflow-hidden rounded-md border border-surface-border bg-white shadow-lg text-left',
            // Hide until coords are calculated to avoid a one-frame flash at 0,0.
            !coords && 'invisible',
          )}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}

export function ActionItem({
  icon: Icon,
  children,
  onClick,
  danger = false,
  disabled = false,
}: {
  icon?: React.ElementType;
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
        danger ? 'text-danger-700 hover:bg-danger-50' : 'hover:bg-surface-muted',
        disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent',
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      <span className="flex-1">{children}</span>
    </button>
  );
}
